import { FilterMatchMode, FilterOperator } from 'primereact/api';
import {
  DataTableFilterMeta,
  DataTableFilterMetaData,
  DataTableOperatorFilterMetaData,
} from 'primereact/datatable';

import { PositionAssignmentRequest } from '@src/main/modules/reports/shared/domain/report.interface';

import { toLocalISODate } from '@src/main/shared/utils/dateUtils';

export type PositionAssignmentFilterMeta = DataTableFilterMeta & {
  global: { value: string | null; matchMode: FilterMatchMode };

  flightNumber: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };

  departureAirport: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };

  arrivalAirport: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };

  aircraftRegistration: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };

  flightDate: {
    operator: FilterOperator;
    constraints: Array<{
      value: [Date | null, Date | null];
      matchMode: FilterMatchMode.BETWEEN;
    }>;
  };
};

export const mapPositionAssignmentFiltersToCriteria = (
  filters: DataTableFilterMeta,
  emptyCriteria: PositionAssignmentRequest,
): PositionAssignmentRequest => {
  const criteria: PositionAssignmentRequest = { ...emptyCriteria };

  const normalize = (value: string | null): string | null =>
    typeof value === 'string' ? value.toUpperCase().trim() : null;

  const fieldMap = {
    flightNumber: 'flightNumber',
    departureAirport: 'departureAirport',
    arrivalAirport: 'arrivalAirport',
    aircraftRegistration: 'aircraftRegistration',
  } as const satisfies Record<
    keyof Pick<
      PositionAssignmentFilterMeta,
      'flightNumber' | 'departureAirport' | 'arrivalAirport' | 'aircraftRegistration'
    >,
    keyof PositionAssignmentRequest
  >;

  Object.keys(fieldMap).forEach((filterKey) => {
    const filter = filters[filterKey as keyof typeof fieldMap];

    const value = filter && 'constraints' in filter ? filter.constraints?.[0]?.value : null;

    if (value !== null) {
      const criteriaKey = fieldMap[filterKey as keyof typeof fieldMap];
      criteria[criteriaKey] = normalize(value);
    }
  });

  const flightDateFilter = filters.flightDate;

  if (isOperatorFilter(flightDateFilter)) {
    const flightDateRange = flightDateFilter.constraints?.[0]?.value;

    if (Array.isArray(flightDateRange)) {
      const [start, end] = flightDateRange;

      if (start) criteria.flightSince = toLocalISODate(start);
      if (end) criteria.flightUntil = toLocalISODate(end);
    }
  }

  return criteria;
};

function isOperatorFilter(
  filter: DataTableFilterMetaData | DataTableOperatorFilterMetaData | undefined,
): filter is DataTableOperatorFilterMetaData {
  return !!filter && 'constraints' in filter;
}
